import { ArrowRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import type { MillFloorUsinage } from "@/features/mill-floor/api/types"
import {
  usinageFullLotCount,
  usinageInputBags,
  usinageInputKg,
  usinageLoadCount,
  usinageOutputKg,
} from "@/features/mill-floor/lib/load-math"
import { usinageStatusLabelKey } from "@/features/mill-floor/lib/status-tones"
import { LOT_TARGET_BAGS } from "@/features/milling/lib/quality"
import { formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"

interface ActiveUsinagesCardProps {
  runs: MillFloorUsinage[]
  isLoading?: boolean
  onSchedule: () => void
  scheduleDisabled?: boolean
}

export function ActiveUsinagesCard({
  runs,
  isLoading,
  onSchedule,
  scheduleDisabled,
}: ActiveUsinagesCardProps) {
  const { t } = useTranslation("milling")
  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight">
          {t("millFloor.active.title")}
        </h2>
        <div className="text-muted-foreground text-xs">
          <span className="text-foreground mr-1 tabular-nums">
            {runs.length}
          </span>
          {t(
            runs.length === 1
              ? "millFloor.active.unitSingular"
              : "millFloor.active.unitPlural",
          )}
        </div>
      </div>

      {runs.length === 0 ? (
        <EmptyState
          title={t("millFloor.active.empty")}
          description={isLoading ? "…" : undefined}
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={onSchedule}
              disabled={scheduleDisabled}
            >
              {t("millFloor.schedule")}
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {runs.map((run) => (
            <UsinageCard key={run.id} run={run} />
          ))}
        </div>
      )}
    </Card>
  )
}

function UsinageCard({ run }: { run: MillFloorUsinage }) {
  const { t } = useTranslation("milling")
  const navigate = useNavigate()
  const statusLabel = t(usinageStatusLabelKey(run.status))

  const inputBags = usinageInputBags(run)
  const inputMT = kgToMt(usinageInputKg(run))
  const outputMT = kgToMt(usinageOutputKg(run))
  const fullCount = usinageFullLotCount(run, LOT_TARGET_BAGS)
  const expected = Math.ceil(inputBags / LOT_TARGET_BAGS)
  const loadCount = usinageLoadCount(run)

  return (
    <button
      type="button"
      onClick={() => navigate(paths.usinageDetail(run.id))}
      className="bg-card hover:bg-muted/40 group flex flex-col overflow-hidden rounded-md border text-left transition-colors"
    >
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold tracking-tight">
            {run.usinageNumber}
          </div>
          <div className="text-muted-foreground mt-0.5 truncate text-xs">
            {run.operator} · {formatDateRange(run.startDate, run.endDate)}
          </div>
        </div>
        <StatusBadge
          tone={run.status === "COMPLETED" ? "success" : "info"}
        >
          {statusLabel}
        </StatusBadge>
      </div>

      <div className="grid grid-cols-3 border-t">
        <Metric
          label={t("millFloor.active.input")}
          value={formatNumber(inputMT, 2)}
          unit={t("millFloor.active.mtShort")}
          sub={t("millFloor.active.bagsShort", { n: formatNumber(inputBags) })}
          rightBorder
        />
        <Metric
          label={t("millFloor.active.output")}
          value={formatNumber(outputMT, 2)}
          unit={t("millFloor.active.mtShort")}
          sub={t("millFloor.active.outputSub", {
            full: fullCount,
            expected: expected || "?",
          })}
          rightBorder
        />
        <Metric
          label={t("millFloor.active.loadsLabel")}
          value={String(loadCount)}
          unit={t(
            loadCount === 1
              ? "millFloor.active.loadSingular"
              : "millFloor.active.loadPlural",
          )}
          sub={t("millFloor.active.loadsSub", {
            expected: expected || "?",
          })}
        />
      </div>

      <div className="bg-muted/30 text-muted-foreground flex items-center justify-between border-t px-4 py-2 text-[10px] font-medium uppercase tracking-wide">
        <span className="truncate">
          {t("millFloor.active.footer", {
            loads: loadCount,
            loadsWord: t(
              loadCount === 1
                ? "millFloor.active.loadSingular"
                : "millFloor.active.loadPlural",
            ),
            expected: expected || "?",
          })}
        </span>
        <span className="inline-flex items-center gap-1">
          {t("millFloor.active.openUsinage")}
          <ArrowRight className="size-3" />
        </span>
      </div>
    </button>
  )
}

function Metric({
  label,
  value,
  unit,
  sub,
  rightBorder,
}: {
  label: string
  value: string
  unit?: string
  sub?: string
  rightBorder?: boolean
}) {
  return (
    <div className={`min-w-0 px-4 py-3 ${rightBorder ? "border-r" : ""}`}>
      <div className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className="text-xl font-semibold tabular-nums leading-none">
          {value}
        </span>
        {unit ? (
          <span className="text-muted-foreground text-[10px] uppercase tracking-wide">
            {unit}
          </span>
        ) : null}
      </div>
      {sub ? (
        <div className="text-muted-foreground mt-0.5 truncate text-[10px] tabular-nums">
          {sub}
        </div>
      ) : null}
    </div>
  )
}

function formatDateRange(start: string, end?: string | null): string {
  const s = String(start).slice(0, 10)
  if (!end) return s
  return `${s} → ${String(end).slice(0, 10)}`
}
